import React from 'react';
import type { AddGroupFormColumn } from '#components/ProTablePlusForm/addGroupTable';
import { FilterType } from '@bedrock/eed-react/lib/components/ProFilter/interface';
import { Radio, Tag } from '@bedrock/components';
import { ThumbsUp, ThumbsDown, Heart, Check, Close } from '@bedrock/icons-react';
import ExpertCell from '../cells/ExpertCell';
import CooperationProjectCell from '../cells/CooperationProjectCell';
import HistoricalCooperationCell from '../cells/HistoricalCooperationCell';
import SupplierCell from '../cells/SupplierCell';
import PlatformPriceCell from '../cells/PlatformPriceCell';
import PlatformCPMCell from '../cells/PlatformCPMCell';
import CustomScreeningCell from '../cells/CustomScreeningCell';
import { CurrencyEnum, PlanSuggestionState } from '#services/api';
import RecommendReturnCell from '../cells/RecommendReturnCell';
import { KOLDemandCategory } from './type';
import InitialQuoteCell from '../cells/InitialQuoteCell';

const PlanSuggestionMap: Record<
  PlanSuggestionState,
  { text: string; color: any; icon: React.ReactNode }
> = {
  3: {
    text: '强推',
    color: 'light-green',
    icon: <ThumbsUp />,
  },
  2: { text: '建议', color: 'light-blue', icon: <Heart /> },
  1: {
    text: '不推荐',
    color: 'light-grey',
    icon: <ThumbsDown />,
  },
};

export const PlanSuggestionOptions = Object.entries(PlanSuggestionMap)
  .map(([key, value]) => ({
    name: value.text,
    icon: value.icon,
    value: Number(key),
  }))
  .reverse();

// ---------------- ExpertView ----------------
export const getKOLColumnsConfig = ({
  viewCurrency,
  view,
  category,
}: {
  viewCurrency: CurrencyEnum;
  view: 'expert' | 'supplier';
  category: KOLDemandCategory;
}): Record<string, AddGroupFormColumn> => {
  return {
    orderNumber: {
      title: '#',
      dataIndex: 'orderNumber',
      width: 42,
      fixed: 'left' as const,
      render: (value, record, index) => {
        return index + 1;
      }
    },
    expert: {
      title: '达人',
      dataIndex: 'main_platform_link',
      width: 256,
      fixed: 'left' as const,
      className: 'align-top',
      render: (value, record, index) => {
        const enabledTags = [
          view === 'expert'
            ? record.$by_plan && 'designated'
            : record.by_plan && 'designated',
          view === 'expert'
            ? record.$by_supplier && 'supplier_recommend'
            : record.by_supplier && 'supplier_recommend',
        ].filter(Boolean) as any[];
        return (
          <ExpertCell
            name={view === 'expert' ? undefined : `达人${record.$index}`}
            enabledTags={enabledTags}
            link={record.main_platform_link}
            platform={
              view === 'expert'
                ? record.$main_platform || record.main_platform
                : record.main_platform
            }
            account={
              view === 'expert'
                ? record.$main_platform_account || record.main_platform_account
                : record.main_platform_account
            }
          />
        );
      },
    },
    demandConfirm: {
      title: '需求方确认',
      dataIndex: 'principal_confirm',
      width: 128,
      fixed: 'left',
      className: 'align-top',
      editRender: (config: any) => {
        return {
          name: 'single-checkbox',
          editable: true,
          props: {
            disabled: !!config?.row?.submit_demand,
            children: '确认',
          },
        }
      },
      batchEditRender: {
        editable: true,
        name: "select",
        props: {
          options: [
            { name: '确认', value: true },
            { name: '不确认', value: false },
          ],
        },
      },
      render: (value, record) => (
        <Tag
          text={value ? '确认' : '未确认'}
          color={value ? 'green' : 'grey'}
          icon={value ? <Check /> : <Close />}
          border={false}
          size="small"
        />
      ),
    },

    planSuggestion: {
      title: '策划预筛选',
      dataIndex: 'plan_suggestion',
      width: 140,
      fixed: 'left',
      className: 'align-top',
      editRender: () => ({
        name: 'radio-group',
        editable: true,
        rule: {
          required: true,
          message: '策划预筛选不能为空',
        },
        props: {
          options: Object.entries(PlanSuggestionMap)
            .map(([key, value]) => ({
              name: (
                <Tag
                  text={value.text}
                  color={value.color as any}
                  size="xSmall"
                  icon={value.icon}
                  className="whitespace-nowrap"
                />
              ),
              value: Number(key),
            }))
            .reverse(),
        },
      }),
      batchEditRender: {
        editable: true,
        name: "select",
        props: {
          options: PlanSuggestionOptions,
          renderLabel: (option) => {
            return (
              <span className="inline-flex items-center gap-[4px] text-color-1">
                {option.icon}
                {option.name}
              </span>
            );
          },
        },
      },
      render: (value) => {
        if (value && !isNaN(Number(value))) {
          return (
            <Tag
              text={PlanSuggestionMap[Number(value)].text}
              color={PlanSuggestionMap[Number(value)].color.replace(
                'light-',
                ''
              )}
              size="small"
              icon={PlanSuggestionMap[Number(value)].icon}
              className="whitespace-nowrap"
            />
          );
        }

        return '--';
      },
    },
    cooperationProject: {
      title: '合作项目',
      dataIndex: 'main_platform_quote_item',
      width: 224,
      className: 'align-top',
      render: (value, record) => (
        <CooperationProjectCell
          mainPlatformType={record.public_order_name}
          quoteProject={record.main_platform_quote_item_name || '--'}
        />
      ),
    },
    followerNum: {
      title: '粉丝数',
      dataIndex: ['demand_source_kol_content_vo', 'follower_num'],
      width: 120,
      className: 'align-top',
      align: 'right' as const,
      render: (value, record) =>
        record?.demand_source_kol_content_vo?.follower_num?.toLocaleString?.() ||
        '--',
    },
    history: {
      title: '历史合作(6个月内)',
      dataIndex: ['demand_source_kol_content_vo', 'is_coop_last6m'],
      className: 'align-top',
      width: 280,
      render: (value, record) => (
        <HistoricalCooperationCell
          isEmpty={record?.demand_source_kol_content_vo?.is_coop_last6m === undefined}
          hasHistory={
            record?.demand_source_kol_content_vo?.is_coop_last6m === '1'
          }
          avgPrice={category === 'live' ? record?.demand_source_kol_content_vo?.coop_price_cny_per_live : record?.demand_source_kol_content_vo?.coop_price_cny_per}
          priceCurrency={'RMB'} // 内容营销价格币种固定为人民币，来自服务端
          avgCPM={record?.demand_source_kol_content_vo?.coop_cpm_six_month}
          avgCPMPV={record?.demand_source_kol_content_vo?.cmp_pv_six_month}
          avgCPMUV={record?.demand_source_kol_content_vo?.cmp_uv_six_month}
          category={category}
          viewCurrency={viewCurrency}
        />
      ),
    },
    supplierCount: {
      title: '提报数量',
      dataIndex: 'quote_count',
      width: 72,
      className: 'align-top',
      align: 'left' as const,
      render: (value, record, index) => value ?? '--',
    },
    supplier: {
      title: '供应商',
      className: 'align-top',
      dataIndex: ['demand_source_supplier_vo', 'supplier_id'],
      width: 240,
      render: (value, record) => (
        <SupplierCell
          record={record}
          viewCurrency={viewCurrency}
          category={category}
        />
      ),
    },
    exclusiveSign: {
      title: '是否独家签约',
      dataIndex: 'exclusive_sign',
      width: 80,
      align: 'left' as const,
      className: 'align-top',
      render: (value) => (
        <Tag
          text={value ? '是' : '否'}
          color={value ? 'light-green' : 'light-grey'}
          size="xSmall"
        />
      ),
    },
    platformPrice: {
      title: '平台价格',
      dataIndex: 'platform_quote_price',
      width: 280,
      className: 'align-top',
      render: (value, record) => (
        <PlatformPriceCell
          starMapPrices={
            record?.demand_source_kol_content_vo?.all_quote_price_cny || []
          }
          starMapCurrency={'RMB'}
          supplierPrices={record?.platform_quote_price}
          supplierCurrency={record.quote_price_currency}
          quoteProject={record?.main_platform_quote_item}
          viewCurrency={viewCurrency}
        />
      ),
    },
    platformCPM: {
      title: '平台CPM',
      dataIndex: 'platformCPMPV',
      width: 240,
      className: 'align-top',
      render: (value, record) => (
        <PlatformCPMCell
          starMapCPMList={
            record?.demand_source_kol_content_vo?.all_cmp_expected || []
          }
          supplierCPMList={record?.platform_cpm}
          quoteProject={record.main_platform_quote_item}
        />
      ),
    },
    platformCPMPV: {
      title: '平台CPM(PV)',
      dataIndex: 'platformCPMUV',
      width: 240,
      className: 'align-top',
      render: (value, record) => (
        <PlatformCPMCell
          starMapCPMList={
            record?.demand_source_kol_content_vo?.all_cmp_expected || []
          }
          supplierCPMList={record?.platform_cpm_pv}
          quoteProject={record.main_platform_quote_item}
        />
      ),
    },
    platformCPMUV: {
      title: '平台CPM(UV)',
      dataIndex: 'platformCPM',
      width: 240,
      className: 'align-top',
      render: (value, record) => (
        <PlatformCPMCell
          starMapCPMList={
            record?.demand_source_kol_content_vo?.all_cmp_expected || []
          }
          supplierCPMList={record?.platform_cpm_uv}
          quoteProject={record.main_platform_quote_item}
        />
      ),
    },
    customScreening: {
      title: '定制排查',
      dataIndex: 'customScreening',
      width: 400,
      className: 'align-top',
      render: (value, record) => (
        <CustomScreeningCell
          screeningName={
            <span className="font-bold">
              {record?.demand_source_custom_inspection_fields_value?.length
                ? `定制排查${record.$custom_inspection_index + 1}`
                : undefined}
            </span>
          }
          rules={(
            record?.demand_source_custom_inspection_fields_value || []
          ).map((f: any) => ({
            ruleName: f.field_name,
            description: f.field_name,
            passed: true,
            actualValue: f.value,
            requiredValue: f.plan_remark,
          }))}
        />
      ),
    },
    supplierName: {
      title: '供应商',
      dataIndex: 'demand_supplier_id',
      width: 120,
      fixed: 'left',
      className: 'align-top',
      render: (value, record) => {
        return (
          <span className="font-bold">{record.demand_source_supplier_vo?.supplier_name ?? '-'}</span>
        );
      },
    },
    initialQuote: {
      title: '初次报价',
      dataIndex: 'quote_price',
      width: 240,
      className: 'align-top',
      render: (value, record) => (
        <InitialQuoteCell
          viewCurrency={viewCurrency}
          quotePrice={record.quote_price}
          quotePriceCurrency={record.quote_price_currency}
          cpm={record.cpm}
          cpmPV={record.cpm_pv}
          cpmUV={record.cpm_uv}
          category={category}
        />
      ),
    },
  };
};
